import {
  ConsoleOutputLevel,
  OutputToConsoleFunc,
  PlatformProfileSource,
  PlatformSource,
  SessionMetadata,
} from "../@types"
import { ApplicationEvents } from "./applicationEvents"
import geoFilter from "./geoFilter"
import makeExecutor from "./makeExecutor"
import { Scheduler } from "./measurementScheduler"
import { PerformanceEntryManager } from "./openinsights/resourceTiming"
import { PageSettings } from "./pageSettings"
import { createPlatform } from "./platform"
import { createPlatformProfile, PlatformProfile } from "./platformProfile"
import {
  createPlatformTestObject,
  PlatformTestObject,
} from "./platformTestObject"
import { ResettableEventCounter } from "./resettableEventCounter"
import { RuntimeValues } from "./runtimeValues"
import { selectWeightedRandomSample } from "./selectRandomSample"
import { SessionConfig } from "./sessionConfig"

export interface Dependencies {
  applicationEvents: ApplicationEvents
  callClearTimeout: (depID: number, timeoutID: number) => void
  callFetch: (url: string, options?: RequestInit) => Promise<Response>
  callSendBeacon: (depID: number, url: string) => boolean
  callSetTimeout: (depID: number, fn: () => void, timeout: number) => number
  callClearResourceTimings: () => void
  measurementScheduler: Scheduler
  newAbortController: () => AbortController
  newDate: (depID: number) => Date
  newEpochTimestamp: (depID: number) => number
  newMonotonicTimestamp: (depID: number) => number
  newRandomNumber: (depID: number) => number
  outputToConsole: OutputToConsoleFunc
  performanceEntryAwaitTimeouts: ResettableEventCounter
  performanceEntryManager: PerformanceEntryManager
  runtimeValues: RuntimeValues
}

const buildPopulation = (
  platformsSource: PlatformSource[],
  profilesSource: Record<string, PlatformProfileSource>,
  sessionConfig: SessionConfig,
  reporterContinent: string,
  reporterCountry: string,
  outputToConsole: OutputToConsoleFunc,
) => {
  const result: PlatformTestObject[] = []
  const platformProfiles: Record<string, PlatformProfile> = {}
  for (const [profileName, source] of Object.entries(profilesSource)) {
    try {
      platformProfiles[profileName] = createPlatformProfile(
        source,
        reporterContinent,
        reporterCountry,
      )
    } catch (e) {
      // If `e` isn't an Error, then there's a bigger problem
      outputToConsole(ConsoleOutputLevel.warn, () => (e as Error).message)
    }
  }
  platformsSource
    .map((config) => {
      try {
        return createPlatform(config, platformProfiles[config.profile])
      } catch (e) {
        // If `e` isn't an Error, then there's a bigger problem
        outputToConsole(ConsoleOutputLevel.warn, () => (e as Error).message)
      }
    })
    .filter((platform) => {
      return platform ? geoFilter(reporterCountry, platform.geoRules) : false
    })
    .flatMap((platform) => {
      if (platform) {
        return (
          platform.platformProfile.profileTestObjects
            // Check for geo-fencing at the test object level
            .filter((profileTestObject) =>
              geoFilter(reporterCountry, profileTestObject.geoRules),
            )
            .map((profileTestObjectConfig) =>
              createPlatformTestObject(
                sessionConfig,
                profileTestObjectConfig,
                platform,
              ),
            )
        )
      }
    })
    .forEach((platformTestObjectOrUndefined) => {
      if (platformTestObjectOrUndefined) {
        result.push(platformTestObjectOrUndefined)
      }
    })
  return result
}

export const initializeScheduledPopulation = (
  dependencies: Dependencies,
  sessionMetadata: SessionMetadata,
  pageSettings: PageSettings,
  platformsSource: PlatformSource[],
  profilesSource: Record<string, PlatformProfileSource>,
  sessionConfig: SessionConfig,
  reporterContinent: string,
  reporterCountry: string,
) => {
  const population = buildPopulation(
    platformsSource,
    profilesSource,
    sessionConfig,
    reporterContinent,
    reporterCountry,
    dependencies.outputToConsole,
  )
  // Draw a weighted random sample for immediate measurement
  const sampleToMeasureImmediately = selectWeightedRandomSample(
    population,
    sessionConfig.siteOwnerSettings.maxInitialTestObjects,
    dependencies.newRandomNumber,
    // Use the target frequency as the "weighting factor". Since this could
    // be a decimal number less than 1, be sure to round up
    (config) => Math.ceil(config.targetFrequency),
    // Expect target frequencies less than 1 per minute
    60,
  )

  dependencies.measurementScheduler.initializePopulation(
    population,
    sessionMetadata,
    sampleToMeasureImmediately,
  )

  // Convert the result to a set of "executables" for OpenInsights to process
  return sampleToMeasureImmediately.map((config) =>
    makeExecutor(
      {
        applicationEvents: dependencies.applicationEvents,
        callClearTimeout: dependencies.callClearTimeout,
        callSendBeacon: dependencies.callSendBeacon,
        callSetTimeout: dependencies.callSetTimeout,
        callClearResourceTimings: dependencies.callClearResourceTimings,
        callFetch: dependencies.callFetch,
        measurementScheduler: dependencies.measurementScheduler,
        newAbortController: dependencies.newAbortController,
        newDate: dependencies.newDate,
        newEpochTimestamp: dependencies.newEpochTimestamp,
        newMonotonicTimestamp: dependencies.newMonotonicTimestamp,
        newRandomNumber: dependencies.newRandomNumber,
        outputToConsole: dependencies.outputToConsole,
        performanceEntryAwaitTimeouts:
          dependencies.performanceEntryAwaitTimeouts,
        performanceEntryManager: dependencies.performanceEntryManager,
        runtimeValues: dependencies.runtimeValues,
      },
      config,
      sessionMetadata,
    ),
  )
}

export const updateScheduledPopulation = (
  measurementScheduler: Scheduler,
  sessionMetadata: SessionMetadata,
  platformsSource: PlatformSource[],
  profilesSource: Record<string, PlatformProfileSource>,
  sessionConfig: SessionConfig,
  reporterContinent: string,
  reporterCountry: string,
  outputToConsole: OutputToConsoleFunc,
) =>
  measurementScheduler.updatePopulation(
    buildPopulation(
      platformsSource,
      profilesSource,
      sessionConfig,
      reporterContinent,
      reporterCountry,
      outputToConsole,
    ),
    sessionMetadata,
  )
